"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ShieldAlert } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const SECTOR_OPTIONS = ["Energia", "Tecnologia", "Agricoltura", "Manifattura", "Servizi", "Altro"];
const BUDGET_OPTIONS = ["Meno di €50.000", "€50.000 - €200.000", "€200.000 - €500.000", "Più di €500.000"];
function CandidaturaFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-700">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/30">
          <ShieldAlert className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif text-2xl font-bold text-foreground">Accesso Richiesto</h3>
          <p className="font-sans text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-border hover:bg-accent hover:text-accent-foreground font-sans tracking-widest uppercase text-xs">
          Nuova Sottomissione
        </Button>
      </div>;
  }
  const minimalistInputClass = "border-x-0 border-t-0 border-b border-border rounded-none bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary shadow-none font-sans text-foreground placeholder:text-muted-foreground/50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="company_name" rules={{
        required: "Il nome dell'azienda è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Nome Azienda</FormLabel>
              <FormControl>
                <Input placeholder="Es. Acme S.p.A." className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider opacity-50">Inserisci il nome legale della tua azienda.</FormDescription>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="sector" rules={{
          required: "Seleziona un settore"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Settore di Attività</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={minimalistInputClass}>
                      <SelectValue placeholder="Seleziona il settore" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-none border-border bg-card">
                    {SECTOR_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="rounded-none font-sans focus:bg-primary/20 focus:text-foreground">
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage className="font-sans text-xs" />
              </FormItem>} />

          <FormField control={control} name="target_market" rules={{
          required: "Il mercato di interesse è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Mercato di Interesse</FormLabel>
                <FormControl>
                  <Input placeholder="Es. CIS, UE, Nord America" className={minimalistInputClass} {...field} />
                </FormControl>
                <FormMessage className="font-sans text-xs" />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="budget_range" rules={{
          required: "Seleziona una fascia di budget"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Fascia di Budget</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={minimalistInputClass}>
                      <SelectValue placeholder="Seleziona il budget" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-none border-border bg-card">
                    {BUDGET_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="rounded-none font-sans focus:bg-primary/20 focus:text-foreground">
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage className="font-sans text-xs" />
              </FormItem>} />

          <FormField control={control} name="contact_email" rules={{
          required: "L'email è obbligatoria",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Indirizzo email non valido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-xs tracking-widest uppercase text-muted-foreground">Email di Contatto</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="nome@azienda.it" className={minimalistInputClass} {...field} />
                </FormControl>
                <FormMessage className="font-sans text-xs" />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive font-sans text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full py-8 h-auto rounded-none bg-primary hover:bg-primary/90 text-primary-foreground font-serif text-lg font-bold tracking-widest uppercase transition-all duration-300 group relative overflow-hidden">
        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
        <span className="relative flex items-center justify-center gap-3">
          {isSubmitting ? <>
              <Loader className="size-5 animate-spin" />
              Elaborazione...
            </> : 'Richiedi Valutazione di Accesso'}
        </span>
      </Button>
    </Form>;
}
export default function CandidaturaOperativa() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="candidatura-operativa" className="dark bg-background text-foreground py-24 md:py-32 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[1px] bg-gradient-to-r from-transparent via-border/30 to-transparent" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[1px] bg-gradient-to-r from-transparent via-border/30 to-transparent" />
      
      <div ref={ref} className={cn("max-w-3xl mx-auto transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <Card className="bg-card/40 backdrop-blur-xl border-border/30 rounded-none shadow-2xl overflow-hidden relative">
          {}
          <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-primary/50" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-primary/50" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-primary/50" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-primary/50" />

          <CardHeader className="pt-12 pb-8 px-8 md:px-16 text-center space-y-6">
            <CardTitle className="font-serif text-4xl md:text-5xl font-bold text-foreground tracking-wide">
              Candidatura Operativa
            </CardTitle>
            <CardDescription className="font-sans italic text-base md:text-lg text-muted-foreground max-w-xl mx-auto">Nota: Vostoka gestisce max 3 espansioni l'anno, budget &gt;5.000€. Per spese più contenute, usa SeoRussia.it.</CardDescription>
          </CardHeader>
          
          <CardContent className="px-8 md:px-16 pb-12">
            <FormProvider formId="candidatura_operativa_form_home" sectionName="Candidatura Operativa" defaultValues={{
            company_name: "",
            sector: "",
            target_market: "",
            budget_range: "",
            contact_email: ""
          }} submitText="Richiedi Valutazione di Accesso" successMessage="Protocollo di valutazione avviato. Il nostro team di intelligence analizzerà i parametri inseriti e ti contatterà a breve." errorMessage="Errore di trasmissione dati. Verifica la connessione e riprova.">
              <CandidaturaFormInner />
            </FormProvider>
          </CardContent>

          <CardFooter className="bg-secondary/30 border-t border-border/30 py-6 px-8 md:px-16 flex justify-center">
            <p className="font-sans text-xs tracking-widest uppercase text-muted-foreground/70 text-center">
              Linea Diretta Operazioni Logistiche (Solo emergenze cargo e sdoganamenti).
            </p>
          </CardFooter>
        </Card>
      </div>
    </section>;
}